/**
 * Tabela "Por empresa" da Central (RNF15 / RNF16), pronta para centenas de
 * empresas: busca, filtro por status, ordenacao por coluna e paginacao.
 *
 * Decisoes nao obvias:
 *  1. O estado (busca, filtro, ordem, pagina) vem da PAGINA, para "O que
 *     fazer agora" e o ranking abrirem a tabela ja recortada. As regras
 *     puras moram em `tabela.ts`; aqui so ha renderizacao.
 *  2. Dez colunas nao cabem em 360px: abaixo de `md` a tabela vira lista de
 *     cartoes com as mesmas medidas (padrao de LeadsTable); acima, rola
 *     dentro de `overflow-x-auto` com o cabecalho visivel. Quem escolhe e o
 *     CSS, nao um matchMedia.
 *  3. "Abrir CRM" e um BOTAO, nao um link: precisa trocar a empresa em foco
 *     (`selectCompany`) antes de navegar, e link nao faz efeito colateral.
 *     "Cadastro" e link puro para /admin/empresas/:id, preservando a busca.
 *  4. O custo e um botao (sublinhado pontilhado) que abre o detalhe por
 *     canal num modal: o detalhe de cada empresa nao cabe na linha e
 *     renderizar todos de uma vez seria pagar por quem ninguem abriu.
 *  5. Um `secondary` e um `ghost` por linha, nunca `primary`: dez botoes
 *     vermelhos numa tabela deixam de apontar a acao principal.
 *  6. O rodape soma o que esta FILTRADO (todas as paginas), e diz isso, para
 *     o total nunca discordar do recorte.
 */

import { useId, useMemo } from 'react';
import { Pagination } from '../../../components/Pagination';
import { Badge, Button, FilterChips, Icon, Input, LinkButton, SectionCard, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDateTime, formatPercentFromBps, formatRatio, VALOR_AUSENTE } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import {
  alternarOrdem,
  COLUNAS,
  ESTADO_INICIAL_DA_TABELA,
  filtrarLinhas,
  ORDEM_DAS_COLUNAS,
  ordenarLinhas,
  passaNoFiltro,
  somarLinhas,
  TAMANHO_DA_PAGINA,
  type ColunaDaTabela,
  type EstadoDaTabela,
  type FiltroDaTabela,
} from '../tabela';
import type { CompanyRow } from '../types';
import { BILLING_LABEL, BILLING_TONE, COMPANY_STATUS_LABEL, COMPANY_STATUS_TONE, EVENT_KIND_LABEL, FUNNEL_ICON, FUNNEL_LABEL } from '../vocabulario';
import { Delta } from './Delta';
import { IdentidadeEmpresa, identidadeDaEmpresa } from './IdentidadeEmpresa';

export function cadastroDaEmpresa(companyId: string): string {
  return `/admin/empresas/${encodeURIComponent(companyId)}`;
}

function taxaDe(row: CompanyRow): string {
  return row.abandonedCount === 0 ? VALOR_AUSENTE : formatPercentFromBps(row.recoveryRateBps);
}

function margemDe(row: CompanyRow): string {
  return row.marginBps === null ? VALOR_AUSENTE : formatPercentFromBps(row.marginBps);
}

function FunisAtivos({ funis }: { readonly funis: CompanyRow['activeFunnels'] }): JSX.Element {
  if (funis.length === 0) {
    return <span className="text-xs text-ink-subtle">Nenhum funil ativo</span>;
  }
  return (
    <ul className="flex items-center gap-1" aria-label="Funis ativos">
      {funis.map((f) => (
        <li key={f} title={FUNNEL_LABEL[f]} className="grid h-5 w-5 place-items-center rounded-md bg-brand/10 text-brand">
          <Icon name={FUNNEL_ICON[f]} size="xs" />
          <span className="sr-only">{FUNNEL_LABEL[f]}</span>
        </li>
      ))}
    </ul>
  );
}

function UltimoEvento({ evento }: { readonly evento: CompanyRow['lastEvent'] }): JSX.Element {
  if (evento === null) {
    return <span className="text-xs text-ink-subtle">Nenhum evento ainda</span>;
  }
  return (
    <span className="block">
      <span className="block tabular-nums text-ink-muted">{formatDateTime(evento.at)}</span>
      <span className="block text-xs text-ink-subtle">{EVENT_KIND_LABEL[evento.kind]}</span>
    </span>
  );
}

/** Dinheiro ou o marcador de ausencia: custo e margem `null` = ainda nao medidos. */
function dinheiroOuAusente(cents: number | null): string {
  return cents === null ? VALOR_AUSENTE : formatBRLFromCents(cents);
}

function BotaoDeCusto({ row, onDetalhar }: { readonly row: CompanyRow; readonly onDetalhar: (row: CompanyRow) => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onDetalhar(row)}
      aria-label={
        row.costCents === null
          ? `Ver uso dos canais de ${row.companyName}: custo ainda não medido`
          : `Detalhar custos de ${row.companyName}: ${formatBRLFromCents(row.costCents)}`
      }
      title={row.costCents === null ? 'Custo ainda não medido; ver o uso por canal' : 'Ver uso e tarifa por canal'}
      className="rounded-md tabular-nums text-ink underline decoration-dotted underline-offset-4 transition-colors hover:text-brand"
    >
      {dinheiroOuAusente(row.costCents)}
    </button>
  );
}

function Acoes({ row, onAbrirCrm }: { readonly row: CompanyRow; readonly onAbrirCrm: (companyId: string) => void }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="secondary" size="sm" icon="externo" onClick={() => onAbrirCrm(row.companyId)} aria-label={`Abrir CRM de ${row.companyName}`}>
        Abrir CRM
      </Button>
      <LinkButton to={cadastroDaEmpresa(row.companyId)} variant="ghost" size="sm" icon="empresa">
        Cadastro<span className="sr-only"> de {row.companyName}</span>
      </LinkButton>
    </div>
  );
}

function Cabecalho({ coluna, estado, onOrdenar }: { readonly coluna: ColunaDaTabela; readonly estado: EstadoDaTabela; readonly onOrdenar: (coluna: ColunaDaTabela) => void }): JSX.Element {
  const info = COLUNAS[coluna];
  const ativa = estado.ordem.coluna === coluna;
  return (
    <th scope="col" aria-sort={ativa ? (estado.ordem.desc ? 'descending' : 'ascending') : 'none'} className={`px-3 py-3 font-medium first:pl-5 ${info.numerica ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onOrdenar(coluna)}
        title={info.titulo}
        className={`inline-flex items-center gap-1 rounded-md uppercase tracking-wide transition-colors hover:text-ink ${ativa ? 'text-ink' : ''} ${info.numerica ? 'flex-row-reverse' : ''}`}
      >
        {info.rotulo}
        <span aria-hidden="true" className={`text-[10px] ${ativa ? '' : 'opacity-40'}`}>
          {ativa ? (estado.ordem.desc ? '▼' : '▲') : '↕'}
        </span>
      </button>
    </th>
  );
}

export function TabelaPorEmpresa({
  rows,
  companies,
  estado,
  onEstado,
  onAbrirCrm,
  onDetalharCustos,
}: {
  readonly rows: readonly CompanyRow[];
  readonly companies: readonly CompanySummary[];
  readonly estado: EstadoDaTabela;
  readonly onEstado: (estado: EstadoDaTabela) => void;
  readonly onAbrirCrm: (companyId: string) => void;
  readonly onDetalharCustos: (row: CompanyRow) => void;
}): JSX.Element {
  const idBusca = useId();

  const filtradas = useMemo(() => filtrarLinhas(rows, estado.busca, estado.filtro), [rows, estado.busca, estado.filtro]);
  const ordenadas = useMemo(() => ordenarLinhas(filtradas, estado.ordem), [filtradas, estado.ordem]);
  const offset = Math.min(estado.offset, Math.max(0, Math.floor((ordenadas.length - 1) / TAMANHO_DA_PAGINA) * TAMANHO_DA_PAGINA));
  const pagina = ordenadas.slice(offset, offset + TAMANHO_DA_PAGINA);
  const soma = somarLinhas(filtradas);

  const contar = (filtro: FiltroDaTabela): string => formatCount(rows.filter((r) => passaNoFiltro(r, filtro)).length);
  const chips: readonly ChipOption<FiltroDaTabela>[] = [
    { value: 'todas', label: 'Todas', count: contar('todas') },
    { value: 'ativa', label: COMPANY_STATUS_LABEL.ativa + 's', count: contar('ativa') },
    { value: 'onboarding', label: COMPANY_STATUS_LABEL.onboarding, count: contar('onboarding') },
    { value: 'pausada', label: COMPANY_STATUS_LABEL.pausada + 's', count: contar('pausada') },
    { value: 'margem_negativa', label: 'Margem negativa', count: contar('margem_negativa') },
  ];

  const ordenar = (coluna: ColunaDaTabela): void => onEstado({ ...estado, ordem: alternarOrdem(estado.ordem, coluna), offset: 0 });
  const filtrar = (filtro: FiltroDaTabela): void => onEstado({ ...estado, filtro, offset: 0 });
  const buscar = (busca: string): void => onEstado({ ...estado, busca, offset: 0 });
  const limpar = (): void => onEstado({ ...ESTADO_INICIAL_DA_TABELA, ordem: estado.ordem });

  const recorteAtivo = estado.busca.trim() !== '' || estado.filtro !== 'todas';

  return (
    <SectionCard
      id="por-empresa"
      title="Por empresa"
      icon="empresa"
      description="Cada empresa cadastrada, com o retorno do período, a comissão, os custos, a margem e o último evento. Clique no cabeçalho para ordenar; clique no custo para detalhar."
      padded={false}
      aside={
        <>
          <FilterChips label="Filtrar empresas por status" options={chips} value={estado.filtro} onChange={filtrar} />
          <div className="relative">
            <label htmlFor={idBusca} className="sr-only">
              Buscar empresa pelo nome
            </label>
            <Icon name="busca" size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
            <Input id={idBusca} size="sm" type="search" placeholder="Buscar empresa" value={estado.busca} onChange={(e) => buscar(e.target.value)} className="w-44 pl-9" />
          </div>
        </>
      }
    >
      {rows.length === 0 ? (
        <div className="flex flex-col items-start gap-3 px-5 pb-5">
          <p className="text-sm text-ink-subtle">Nenhuma empresa cadastrada ainda. A primeira implantação começa pelo cadastro; a partir dela, cada abandono vira uma chance de comissão.</p>
          <LinkButton to="/admin/empresas/nova" variant="secondary" size="sm" icon="mais">
            Nova empresa
          </LinkButton>
        </div>
      ) : filtradas.length === 0 ? (
        <div className="flex flex-col items-start gap-3 px-5 pb-5">
          <p className="text-sm text-ink-subtle">
            Nenhuma empresa combina com {estado.busca.trim() === '' ? 'este filtro' : `«${estado.busca.trim()}»`}
            {estado.filtro === 'todas' || estado.busca.trim() === '' ? '' : ' neste filtro'}.
          </p>
          <Button variant="ghost" size="sm" icon="x" onClick={limpar}>
            Limpar filtros
          </Button>
        </div>
      ) : (
        <>
          {/* Desktop */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[76rem] border-collapse text-sm">
              <caption className="sr-only">
                Empresas da plataforma com status, entradas, recuperados, taxa, recuperado bruto, comissão, custos, margem e último evento no período. Ordenável por coluna.
              </caption>
              <thead>
                <tr className="border-y border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
                  {ORDEM_DAS_COLUNAS.map((coluna) => (
                    <Cabecalho key={coluna} coluna={coluna} estado={estado} onOrdenar={ordenar} />
                  ))}
                  <th scope="col" className="px-5 py-3 font-medium">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pagina.map((row) => {
                  const identidade = identidadeDaEmpresa(row.companyId, companies, row.companyName);
                  return (
                    <tr key={row.companyId} className="border-b border-line/60 transition-colors last:border-0 hover:bg-ink/5 focus-within:bg-ink/5">
                      <th scope="row" className="px-5 py-3 text-left font-normal">
                        <IdentidadeEmpresa empresa={identidade} size="sm" />
                        <div className="mt-1 pl-10">
                          <FunisAtivos funis={row.activeFunnels} />
                        </div>
                      </th>
                      <td className="px-3 py-3">
                        <span className="flex flex-col items-start gap-1">
                          <Badge tone={COMPANY_STATUS_TONE[row.status]} dot>
                            {COMPANY_STATUS_LABEL[row.status]}
                          </Badge>
                          <span title={`Modelo de cobrança: ${BILLING_LABEL[row.billingModel]}`}>
                            <Badge tone={BILLING_TONE[row.billingModel]}>{BILLING_LABEL[row.billingModel]}</Badge>
                          </span>
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-ink">{formatCount(row.abandonedCount)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-ink">{formatCount(row.recoveredCount)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-ink-muted">{taxaDe(row)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        <span className="block font-medium text-ink">{formatBRLFromCents(row.recoveredAmountCents)}</span>
                        <Delta atual={row.recoveredAmountCents} anterior={row.previous === null ? null : row.previous.recoveredAmountCents} tipo="dinheiro" compacto />
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        <span className="block text-ink">{formatBRLFromCents(row.commissionCents)}</span>
                        <span className="block text-xs text-ink-subtle">{formatPercentFromBps(row.feeBps)}</span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <BotaoDeCusto row={row} onDetalhar={onDetalharCustos} />
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        <span className={`block font-medium ${row.marginCents !== null && row.marginCents < 0 ? 'text-status-critical' : 'text-ink'}`}>{dinheiroOuAusente(row.marginCents)}</span>
                        <span className="block text-xs text-ink-subtle">{margemDe(row)}</span>
                      </td>
                      <td className="px-3 py-3">
                        <UltimoEvento evento={row.lastEvent} />
                      </td>
                      <td className="px-5 py-3">
                        <Acoes row={row} onAbrirCrm={onAbrirCrm} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-line bg-elevated text-xs">
                  <th scope="row" className="px-5 py-3 text-left font-medium text-ink-muted">
                    Total {recorteAtivo ? 'do que está filtrado' : 'geral'} ({formatCount(filtradas.length)} {filtradas.length === 1 ? 'empresa' : 'empresas'})
                  </th>
                  <td className="px-3 py-3" />
                  <td className="px-3 py-3 text-right font-medium tabular-nums text-ink">{formatCount(soma.abandonados)}</td>
                  <td className="px-3 py-3 text-right font-medium tabular-nums text-ink">{formatCount(soma.recuperados)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-ink-muted">{formatRatio(soma.recuperados, soma.abandonados)}</td>
                  <td className="px-3 py-3 text-right font-medium tabular-nums text-ink">{formatBRLFromCents(soma.recuperadoCents)}</td>
                  <td className="px-3 py-3 text-right font-medium tabular-nums text-ink">{formatBRLFromCents(soma.comissaoCents)}</td>
                  <td className="px-3 py-3 text-right font-medium tabular-nums text-ink">{dinheiroOuAusente(soma.custoCents)}</td>
                  <td className={`px-3 py-3 text-right font-medium tabular-nums ${soma.margemCents !== null && soma.margemCents < 0 ? 'text-status-critical' : 'text-ink'}`}>{dinheiroOuAusente(soma.margemCents)}</td>
                  <td className="px-3 py-3" colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Mobile */}
          <ul className="flex flex-col gap-3 px-4 pb-4 md:hidden">
            {pagina.map((row) => {
              const identidade = identidadeDaEmpresa(row.companyId, companies, row.companyName);
              return (
                <li key={row.companyId} className="flex flex-col gap-3 rounded-xl border border-line bg-surface px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <IdentidadeEmpresa empresa={identidade} size="md" />
                    <Badge tone={COMPANY_STATUS_TONE[row.status]} dot>
                      {COMPANY_STATUS_LABEL[row.status]}
                    </Badge>
                  </div>

                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <dt className="text-xs text-ink-subtle">Entradas</dt>
                      <dd className="tabular-nums text-ink">{formatCount(row.abandonedCount)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-ink-subtle">Recuperados</dt>
                      <dd className="tabular-nums text-ink">
                        {formatCount(row.recoveredCount)} <span className="text-ink-subtle">· {taxaDe(row)}</span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-ink-subtle">Recuperado bruto</dt>
                      <dd className="tabular-nums font-medium text-ink">{formatBRLFromCents(row.recoveredAmountCents)}</dd>
                      <dd>
                        <Delta atual={row.recoveredAmountCents} anterior={row.previous === null ? null : row.previous.recoveredAmountCents} tipo="dinheiro" compacto />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-ink-subtle">Comissão · {formatPercentFromBps(row.feeBps)}</dt>
                      <dd className="tabular-nums text-ink">{formatBRLFromCents(row.commissionCents)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-ink-subtle">Custos</dt>
                      <dd>
                        <BotaoDeCusto row={row} onDetalhar={onDetalharCustos} />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-ink-subtle">Margem · {margemDe(row)}</dt>
                      <dd className={`tabular-nums font-medium ${row.marginCents !== null && row.marginCents < 0 ? 'text-status-critical' : 'text-ink'}`}>{dinheiroOuAusente(row.marginCents)}</dd>
                    </div>
                  </dl>

                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-line pt-3">
                    <div className="flex items-center gap-3">
                      <FunisAtivos funis={row.activeFunnels} />
                      <Badge tone={BILLING_TONE[row.billingModel]}>{BILLING_LABEL[row.billingModel]}</Badge>
                    </div>
                    <div className="text-right text-xs">
                      <UltimoEvento evento={row.lastEvent} />
                    </div>
                  </div>

                  <Acoes row={row} onAbrirCrm={onAbrirCrm} />
                </li>
              );
            })}
          </ul>

          <div className="px-5 pb-4">
            <Pagination total={ordenadas.length} limit={TAMANHO_DA_PAGINA} offset={offset} onChange={(novo) => onEstado({ ...estado, offset: novo })} />
          </div>
        </>
      )}
    </SectionCard>
  );
}
